import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Controller } from "react-hook-form";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useGetFacilityEmployeesQuery } from "store/apis/OrdersApi";
import { REQUIRED_ERROR } from "utils/errorMessages";
import BaseAutoComplete from "components/shared/baseForm/BaseAutoComplete";
import AddEmployeeModal from "components/shared/addEmployeeModal/AddEmployeeModal";

export default function EmployeeOrder({
  control,
  errors,
  guestBillingTypeId,
  chargesByType,
  facilityId,
  setValue,
  watch,
}) {
  
  const [addOpen, setAddOpen] = useState(false);

  const { data: employees, 
    isFetching: fetchingEmployees 
  } = useGetFacilityEmployeesQuery(
    facilityId,
    { skip: !facilityId }
  );

  const employeeId = watch("employeeId");

  useEffect(() => {
    if (employeeId && employees) {
      const selectedEmployee = employees.find(emp => emp.id === employeeId);
      if (selectedEmployee) {
        setValue("guestName", selectedEmployee.label);
      }
    }
  }, [employeeId, employees, setValue]);

  return (
    <>
      <Grid
        container
        spacing={2}
        alignItems="flex-start"
        justifyContent="center"
        sx={{ marginBottom: "20px" }}
      >
        <Grid size={5}>
          <BaseAutoComplete
            options={employees || []}
            control={control}
            loading={fetchingEmployees}
            formSelectProps={{
              props: {
                name: "employeeId",
                size: "small",
              },
              validationProps: {
                required: REQUIRED_ERROR("Employee"),
                validate: (value) => !!value || REQUIRED_ERROR("Employee"),
              },
            }}
            errors={errors}
            handleSearch={() => {}}
            placeholder="Search or Select Employee"
          />
        </Grid>
        <Grid size={2}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setAddOpen(true)}
            sx={{
              textTransform: "uppercase",
              borderRadius: "8px",
              whiteSpace: "nowrap",
              marginTop: ".5rem"
            }}>
            Add New Employee
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{ marginBottom: "20px" }}>
        <Grid size={3}>
          <Typography sx={{ textAlign: "right", fontWeight: "500" }}>
            Employee Charge:
          </Typography>
        </Grid>
        <Grid size={3}>
          <Controller
            name="guestBillingTypeId"
            control={control}
            defaultValue={guestBillingTypeId ?? 0}
            rules={{
              required: REQUIRED_ERROR("Employee Charge"),
              validate: (value) => value !== 0 || REQUIRED_ERROR("Charge Type"),
            }}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error}>
                <Select
                  {...field}
                  value={field.value ?? 0}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  id="guestBillingTypeId"
                  variant="outlined"
                  size="small"
                  fullWidth
                  displayEmpty
                  sx={{
                    borderRadius: "8px",
                  }}
                >
                  <MenuItem value={0} disabled>
                    Select Charge
                  </MenuItem>
                  {chargesByType?.map((charge) => {
                    return (
                      <MenuItem key={charge.id} value={charge.id}>
                        {charge.chargeText}
                      </MenuItem>
                    );
                  })}
                </Select>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
      </Grid>
      <AddEmployeeModal 
        open={addOpen} 
        onSaved={() => setAddOpen(false)} 
        facilityId={facilityId} 
      />
    </>
  );
}
